import type { ComponentProps, ElementType } from 'react';

import { cn } from '../../lib/utils';
import { Button as ButtonPrimitive } from '../ui/button';

export type IconButtonProps = Omit<ComponentProps<'button'>, 'children'> & {
  /** The icon (a Phosphor icon, or any component taking className). */
  icon: ElementType<{ className?: string }>;
  /**
   * Accessible name - required, since there is no visible label. Pair it with
   * a Tooltip showing the same text.
   */
  'aria-label': string;
  /** Figma "Style". Ghost in toolbars, Outline beside inputs. */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  /** Figma "Size": Small 32 · Medium 40 · Large 48 (icons 16 / 20 / 24). */
  size?: 'sm' | 'md' | 'lg';
  /** Render the single child element (e.g. a link) as the button. */
  asChild?: boolean;
};

/** Icon-only action (Figma: Components ↳ Button ↳ Icon Button). */
export function IconButton({
  icon: Icon,
  variant = 'ghost',
  size = 'md',
  className,
  ...props
}: IconButtonProps) {
  return (
    <ButtonPrimitive
      variant={variant}
      size={`icon-${size}`}
      // Ghost icons sit one step quieter than the ghost label colour (icon/secondary).
      className={cn(variant === 'ghost' && 'text-icon-secondary', className)}
      {...props}
    >
      <Icon aria-hidden />
    </ButtonPrimitive>
  );
}
